import _ from 'lodash'
import ssapi from 'lib/api/secondstepApi'
import { NetSuiteCustomer, SchoolLookUpSiteData } from 'types'

// ============================================================================
// NETSUITE CUSTOMER TRANSFORMATION
// ============================================================================

/**
 * Transforms a NetSuite customer object into the format used by SchoolLookup and forms
 */
export const transformNetSuiteCustomerForForm = (
  customer: NetSuiteCustomer,
): SchoolLookUpSiteData => {
  const { address } = customer || {}
  const { city, country, region } = address || {}

  return {
    city: city || '',
    companyName: customer?.companyName || '',
    country: country || '',
    netsuiteCustomerId: customer?.id || null,
    nsCustomerId: customer?.nsCustomerId || '',
    region: region || '',
    organizationName: customer?.companyName || '',
  }
}

// ============================================================================
// NETSUITE CUSTOMER SEARCH (DEBOUNCED)
// ============================================================================

interface SearchParams {
  nsCustomerId: string
  resolve: (response: any) => void
  reject: (error: any) => void
}

const _searchNSCustomersDebounced = _.debounce(
  async ({ nsCustomerId, resolve, reject }: SearchParams) => {
    try {
      const response = await ssapi.getNetSuiteCustomers({ nsCustomerId })
      resolve(response)
    } catch (err) {
      reject(err)
    }
  },
  300,
)

/**
 * Debounced search for NetSuite customers by nsCustomerId
 */
export const searchNSCustomersDebounced = (nsCustomerId: string): Promise<any> => {
  return new Promise((resolve, reject) =>
    _searchNSCustomersDebounced({ nsCustomerId, resolve, reject }),
  )
}

// ============================================================================
// SITE CHANGE DETECTION
// ============================================================================

/**
 * Checks if site data has changed from initial values
 * @param currentSiteData - Current site data to check
 * @param initialSiteData - Initial site data for comparison
 * @param isInitialLoad - Whether we're still in initial load phase
 */
export const hasSiteChanged = (
  currentSiteData: Partial<SchoolLookUpSiteData>,
  initialSiteData: Partial<SchoolLookUpSiteData>,
  isInitialLoad: boolean = false,
): boolean => {
  // Don't show changes during initial load
  if (isInitialLoad) {
    return false
  }

  const keyFields: Array<keyof SchoolLookUpSiteData> = [
    'nsCustomerId',
    'netsuiteCustomerId',
    'organizationName',
    'companyName',
  ]

  return keyFields.some(field => {
    const currentValue = currentSiteData[field] || ''
    const initialValue = initialSiteData[field] || ''
    return currentValue !== initialValue
  })
}

